import React, { useEffect, useState } from "react";
import axios from "axios";
import ConfirmationDialog from "./ConfirmationDialog";
import DuplicatePathologyDialog from "./DuplicatePathologyDialog";

import "./PathologyCard.css";

const PathologyCard = ({ isOpen, togglePopup, selectedPathology, onDeleteSuccess, onDuplicateSuccess }) => {

  const [formData, setFormData] = useState({
    pathologie: "",
    max_number_of_rules: "",
    age_specifique_pathologie: "",
    sexe_specifique_pathologie: "",
    patho_associées: "",
    red_flags: "",
    green_flags: "",
    min_age_pathologie: "",
    max_age_pathologie: "",
    appareilId: "",
  });
  const [appareils, setAppareils] = useState([]);
  const [medicamentVariants, setMedicamentVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [variantsError, setVariantsError] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  useEffect(() => {
    const fetchAppareils = async () => {
      try {
        const response = await axios.get("/appareils", {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        });
        setAppareils(response.data || []);
      } catch (error) {
        console.error("Error fetching appareils:", error);
      }
    };

    fetchAppareils();
  }, [token]);

  useEffect(() => {
    if (selectedPathology) {
      setFormData({
        pathologie: selectedPathology.pathologie || "",
        max_number_of_rules: selectedPathology.max_number_of_rules ?? "",
        age_specifique_pathologie: selectedPathology.age_specifique_pathologie || "",
        sexe_specifique_pathologie: selectedPathology.sexe_specifique_pathologie || "",
        patho_associées: selectedPathology.patho_associées || "",
        red_flags: selectedPathology.red_flags || "",
        green_flags: selectedPathology.green_flags || "",
        min_age_pathologie: selectedPathology.min_age_pathologie || "",
        max_age_pathologie: selectedPathology.max_age_pathologie || "",
        appareilId: selectedPathology.appareilId?._id || selectedPathology.appareilId || "",
      });
    }
  }, [selectedPathology]);

  useEffect(() => {
    const fetchMedicamentVariants = async () => {
      if (!isOpen || !selectedPathology?._id) {
        setMedicamentVariants([]);
        setVariantsError("");
        return;
      }

      setVariantsLoading(true);
      setVariantsError("");

      try {
        const response = await axios.get(`/pathologies/${selectedPathology._id}/medicament-variants`, {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        });
        setMedicamentVariants(response.data || []);
      } catch (error) {
        setMedicamentVariants([]);
        setVariantsError("Impossible de charger les medicament_variants associés.");
        console.error("Error fetching medicament variants:", error);
      } finally {
        setVariantsLoading(false);
      }
    };

    fetchMedicamentVariants();
  }, [isOpen, selectedPathology, token]);

  if (!isOpen || !selectedPathology) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePathologyUpdate = async () => {
    try {
      const response = await axios.patch(
        `/pathologies/${selectedPathology._id}`,
        formData,
        {
            headers: {
                "x-access-token": token,
                Authorization: `Bearer ${token}`,
            },
        }
    );
      if (response.status === 200) {
        togglePopup();
      }
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  const handleDeletePathology = async () => {
    try {
      const response = await axios.delete(`/pathologies/${selectedPathology._id}`, {
        headers: {
          "x-access-token": token,
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 200) {
        setShowDeleteConfirmation(false);
        onDeleteSuccess(selectedPathology.pathologie);
        togglePopup();
      }
    } catch (error) {
      console.error("Error deleting pathology. Please try again later.");
    }
  };

  return (
    <>
      <div className="popup-overlay" onClick={togglePopup}>
        <div className="popup-content" onClick={(e) => e.stopPropagation()}>
          <div className="pathology-details">
            <div className="pathology-form-grid">
              <label className="form-group">
                <strong>Nom de la pathologie:</strong>
                <input
                  type="text"
                  name="pathologie"
                  value={formData.pathologie}
                  onChange={handleChange}
                />
              </label>
              <label className="form-group">
                <strong>Appareil:</strong>
                <select
                  name="appareilId"
                  value={formData.appareilId}
                  onChange={handleChange}
                >
                  <option value="">Choisir un appareil</option>
                  {appareils.map((appareil) => (
                    <option key={appareil._id} value={appareil._id}>
                      {appareil.appareil}
                    </option>
                  ))}
                </select>
              </label>
              <label className="form-group">
                <strong>Nombre max de règles :</strong>
                <input
                  type="number"
                  name="max_number_of_rules"
                  value={formData.max_number_of_rules}
                  onChange={handleChange}
                  min={0}
                  step={1}
                />
              </label>
              <label className="form-group">
                <strong>Age spécifique:</strong>
                <select
                  name="age_specifique_pathologie"
                  value={formData.age_specifique_pathologie}
                  onChange={handleChange}
                >
                  <option value="">Aucun</option>
                  <option value="A">Adulte</option>
                  <option value="E">Enfant</option>
                </select>
              </label>
              <label className="form-group">
                <strong>Sexe spécifique:</strong>
                <select
                  name="sexe_specifique_pathologie"
                  value={formData.sexe_specifique_pathologie}
                  onChange={handleChange}
                >
                  <option value="">Aucun</option>
                  <option value="H">Homme</option>
                  <option value="F">Femme</option>
                </select>
              </label>
              <label className="form-group full-width">
                <strong>Pathologies associées:</strong>
                <textarea
                  name="patho_associées"
                  value={formData.patho_associées}
                  onChange={handleChange}
                  rows={2}
                />
              </label>
              <label className="form-group full-width">
                <strong>Red flags:</strong>
                <textarea
                  name="red_flags"
                  value={formData.red_flags}
                  onChange={handleChange}
                  rows={2}
                />
              </label>
              <label className="form-group full-width">
                <strong>Green flags:</strong>
                <textarea
                  name="green_flags"
                  value={formData.green_flags}
                  onChange={handleChange}
                  rows={2}
                />
              </label>
              <label className="form-group">
                <strong>Min Age:</strong>
                <input
                  type="text"
                  name="min_age_pathologie"
                  value={formData.min_age_pathologie}
                  onChange={handleChange}
                />
              </label>
              <label className="form-group">
                <strong>Max Age:</strong>
                <input
                  type="text"
                  name="max_age_pathologie"
                  value={formData.max_age_pathologie}
                  onChange={handleChange}
                />
              </label>
            </div>

            <details className="variants-collapse">
              <summary>
                Medicament variants associés ({medicamentVariants.length})
              </summary>
              <div className="variants-content">
                {variantsLoading && <p>Chargement des variants...</p>}
                {!variantsLoading && variantsError && <p className="variants-error">{variantsError}</p>}
                {!variantsLoading && !variantsError && medicamentVariants.length === 0 && (
                  <p>Aucun medicament_variant associé à cette pathologie.</p>
                )}
                {!variantsLoading && !variantsError && medicamentVariants.length > 0 && (
                  <ul className="variants-list">
                    {medicamentVariants.map((variant) => {
                      const medicament = variant.medicamentId || {};
                      const medicamentLabel =
                        medicament.specialite ||
                        medicament.medoc_ou_complement_ou_phyto ||
                        medicament.name ||
                        "Médicament sans nom";

                      return (
                        <li key={variant._id} className="variant-item">
                          <div className="variant-title">{medicamentLabel}</div>
                          <div className="variant-meta">
                            <span>Dose: {variant.dose || "-"}</span>
                            <span>Fréquence: {variant.frequence || "-"}</span>
                            <span>Durée: {variant.duree_traitement || "-"}</span>
                            <span>Conseil: {variant.conseil_utilisation || "-"}</span>
                            <span>Âge: {variant.age_specifique_medic || "-"}</span>
                            <span>Sexe: {variant.sexe_specifique_medic || "-"}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </details>

            <div className="pathology-timestamps">
              <p>
                <strong>Créé à:</strong>{" "}
                {new Date(selectedPathology.createdAt).toLocaleString('en-GB')}
              </p>
              <p>
                <strong>Dernière mise à jour:</strong>{" "}
                {new Date(selectedPathology.updatedAt).toLocaleString('en-GB')}
              </p>
            </div>
          </div>
          <div className="button-group">
            <button className="close-popup" onClick={togglePopup} type="button">
              Fermer
            </button>
            <button
              className="delete-popup"
              onClick={() => setShowDeleteConfirmation(true)}
              type="button"
            >
              Supprimer
            </button>
            <button className="save-popup" onClick={handlePathologyUpdate} type="button">
              Enregistrer
            </button>
            <button
              className="duplicate-popup"
              onClick={() => setShowDuplicateDialog(true)}
            >
              Dupliquer
            </button>
          </div>
        </div>
      </div>
      <ConfirmationDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeletePathology}
        title="Confirmer la suppression"
        message={
          <span>
          Êtes-vous sûr de vouloir supprimer la pathologie <b>{selectedPathology.pathologie}</b>? Cette action est irréversible. <br />Tous les variants associés seront également supprimés définitivement.
          </span>
          }
        confirmText="Supprimer"
        confirmColor="error"
      />
      <DuplicatePathologyDialog
        isOpen={showDuplicateDialog}
        togglePopup={() => setShowDuplicateDialog(false)}
        selectedPathology={selectedPathology}
        onDuplicateSuccess={(duplicatedPathology) => {
          setShowDuplicateDialog(false);
          togglePopup();
          if (onDuplicateSuccess) {
            onDuplicateSuccess(duplicatedPathology);
          }
        }}
      />
    </>
  );
};

export default PathologyCard;
